import React from 'react';
import { ArrowLeft, Flame, Trash2, AlertTriangle, CheckCircle, Mail } from 'lucide-react';
import { Button } from './ui/Button';
import { ThemeToggle } from './ui/ThemeToggle';

interface DeleteAccountProps {
  onBack?: () => void;
  onLogin?: () => void;
}

const Step: React.FC<{ number: number; title: string; children: React.ReactNode }> = ({
  number,
  title,
  children,
}) => (
  <div className="flex space-x-4">
    <div className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
      {number}
    </div>
    <div>
      <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{title}</h3>
      <div className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{children}</div>
    </div>
  </div>
);

export const DeleteAccount: React.FC<DeleteAccountProps> = ({ onBack, onLogin }) => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {onBack && (
              <button
                onClick={onBack}
                className="flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5 mr-1" />
                <span className="text-sm font-medium">Back</span>
              </button>
            )}
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                <Flame className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-bold text-gray-900 dark:text-white">Koru</span>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title */}
        <div className="mb-10">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white">
              Delete Your Koru Account
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            We're sorry to see you go. This page explains how to delete your Koru account, what
            data is removed, and what happens during the process.
          </p>
        </div>

        <div className="space-y-6">
          {/* How to delete in-app */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              How to Delete Your Account
            </h2>
            <div className="space-y-6">
              <Step number={1} title="Sign in to Koru">
                Open the Koru app or visit the web app and sign in with your email and password, or
                Google account.
                {onLogin && (
                  <div className="mt-3">
                    <Button onClick={onLogin} size="sm" variant="outline">
                      Sign In to Koru
                    </Button>
                  </div>
                )}
              </Step>
              <Step number={2} title="Go to your Profile">
                Tap your profile icon or avatar in the top-right corner of the dashboard to open
                your profile settings.
              </Step>
              <Step number={3} title='Find "Delete Account"'>
                Scroll to the bottom of your profile page and tap{' '}
                <strong className="text-gray-800 dark:text-gray-100">Delete Account</strong>. This
                option is clearly marked in red.
              </Step>
              <Step number={4} title="Confirm the deletion">
                You will be asked to enter your current password and type your email address or the
                word <strong className="text-gray-800 dark:text-gray-100">DELETE</strong> to
                confirm. This prevents accidental deletions.
              </Step>
              <Step number={5} title="Account scheduled for deletion">
                Your account enters a{' '}
                <strong className="text-gray-800 dark:text-gray-100">30-day grace period</strong>.
                You can log back in at any time during this window to cancel the deletion and restore
                your account.
              </Step>
            </div>
          </div>

          {/* What gets deleted */}
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Data Permanently Deleted
                </h2>
              </div>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                {[
                  'Your account profile (name, email)',
                  'All habits and configurations',
                  'Entire habit completion history',
                  'Streak records and statistics',
                  'Notes attached to completions',
                  'Notification preferences',
                  'Account settings and preferences',
                  'Google OAuth link (if used)',
                ].map((item) => (
                  <li key={item} className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full mt-1.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center space-x-2 mb-4">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  Retention & Billing
                </h2>
              </div>
              <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-start space-x-2">
                  <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-1.5 flex-shrink-0" />
                  <span>
                    <strong className="text-gray-800 dark:text-gray-100">30-day grace period</strong>{' '}
                    — your data is preserved and your account can be restored during this window
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-1.5 flex-shrink-0" />
                  <span>
                    <strong className="text-gray-800 dark:text-gray-100">Active subscriptions</strong>{' '}
                    — cancel your subscription before deleting your account to avoid further charges.
                    Refunds are subject to Stripe's and the app store's refund policies.
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 flex-shrink-0" />
                  <span>
                    All personal data is fully purged within{' '}
                    <strong className="text-gray-800 dark:text-gray-100">30 days</strong> of
                    confirmed deletion. Anonymized, aggregated analytics (not linked to you) may be
                    retained.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Can't access the app */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
            <div className="flex items-center space-x-2 mb-4">
              <Mail className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Can't Access Your Account?
              </h2>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              If you're unable to sign in to delete your account yourself (e.g. you've lost access
              to your email or password), contact us directly and we will delete your account and all
              associated data on your behalf. Include your registered email address in the message.
            </p>
            <a
              href="mailto:support@dailymobilellc.com?subject=Account Deletion Request"
              className="inline-flex items-center space-x-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium text-sm transition-colors"
            >
              <Mail className="w-4 h-4" />
              <span>Email support@dailymobilellc.com</span>
            </a>
          </div>
        </div>

        {onBack && (
          <div className="mt-8 text-center">
            <Button onClick={onBack} variant="outline" leftIcon={<ArrowLeft size={16} />}>
              Back to Koru
            </Button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            © {new Date().getFullYear()} Koru. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};
